import React, { useState, useEffect, useRef } from "react";
import * as S from "./AdminThemeFacilityModal.styled";
import { facilityService, FacilityDetail } from "services/facilityService";
import { adminThemeService } from "services/adminThemeService";

interface Props {
  themeId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const AdminThemeFacilityModal: React.FC<Props> = ({ themeId, onClose, onSuccess }) => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<FacilityDetail[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!keyword.trim()) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await facilityService.searchFacilities(keyword);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [keyword]);

  const handleAdd = async (facilityId: number) => {
    setAddingId(facilityId);
    try {
      await adminThemeService.addFacility(themeId, facilityId);
      setAddedIds((prev) => new Set(prev).add(facilityId));
      onSuccess();
    } catch {
      // 실패 시 버튼 복구
    } finally {
      setAddingId(null);
    }
  };

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ModalBox onClick={(e) => e.stopPropagation()}>

        <S.ModalHeader>
          <S.ModalTitle>시설 추가</S.ModalTitle>
          <S.CloseButton onClick={onClose}>✕</S.CloseButton>
        </S.ModalHeader>

        <S.ModalBody>
          <S.SearchWrap>
            <S.SearchIcon>🔍</S.SearchIcon>
            <S.SearchInput
              placeholder="시설명으로 검색..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              autoFocus
            />
          </S.SearchWrap>

          <S.ResultList>
            {isSearching ? (
              <S.StatusText>검색 중...</S.StatusText>
            ) : !keyword.trim() ? (
              <S.StatusText>시설명을 입력하여 검색하세요.</S.StatusText>
            ) : results.length === 0 ? (
              <S.StatusText>검색 결과가 없습니다.</S.StatusText>
            ) : (
              results.map((f) => {
                const isDone = addedIds.has(f.id);
                return (
                  <S.ResultItem key={f.id}>
                    {f.imageUrl ? (
                      <S.ResultThumb src={f.imageUrl} alt={f.name} />
                    ) : (
                      <S.ResultThumbPlaceholder>🏛️</S.ResultThumbPlaceholder>
                    )}
                    <S.ResultInfo>
                      <S.ResultName>{f.name}</S.ResultName>
                      <S.ResultSub>{f.building?.name ?? "건물 미배정"}</S.ResultSub>
                    </S.ResultInfo>
                    <S.AddButton
                      isDone={isDone}
                      onClick={() => !isDone && handleAdd(f.id)}
                      disabled={addingId === f.id || isDone}
                    >
                      {addingId === f.id ? "추가 중..." : isDone ? "완료 ✓" : "+ 추가"}
                    </S.AddButton>
                  </S.ResultItem>
                );
              })
            )}
          </S.ResultList>
        </S.ModalBody>

      </S.ModalBox>
    </S.ModalOverlay>
  );
};

export default AdminThemeFacilityModal;
